/**
 * The quick voice menus (Discord's): the input menu (the mic's caret, and a
 * right-click or long-press on mute) and the output menu (a right-click or
 * long-press on deafen), in the room's control bar, the dock and a DM
 * call. Each picks its device in place, without a trip to Settings.
 */
import { tr, useI18n } from '../i18n';
import { openSettings } from '../store/ui';
import { applyDevices, setOutputVolume, setupInput } from '../voice/controller';
import { canPickOutput, listDevices, onDevicesChange } from '../voice/devices';
import { useVoice, type VoicePrefs } from '../voice/store';
import { MAX_USER_VOLUME } from '../voice/types';
import { openMenu, type LiveMenuItems, type MenuItem } from '../ui/overlay';

type AudioKind = 'audioinput' | 'audiooutput';

/** The devices last listed, by kind: a menu opens with these while it lists them again. */
const known: Record<AudioKind, MediaDeviceInfo[]> = { audioinput: [], audiooutput: [] };
const listeners = new Set<() => void>();

/** Lists the devices again; the open device menus follow. */
function refreshDevices(): void {
  void listDevices().then((l) => {
    known.audioinput = l.filter((d) => d.kind === 'audioinput');
    known.audiooutput = l.filter((d) => d.kind === 'audiooutput');
    for (const fn of listeners) fn();
  });
}

/**
 * Items that follow the voice prefs, the language and (with `devices`) the
 * device list while the menu is open: rebuilt only when `key` changes.
 */
function liveItems(key: () => string, build: () => MenuItem[], devices = false): LiveMenuItems {
  let k: string | null = null;
  let items: MenuItem[] = [];
  return {
    subscribe(onChange) {
      const offs = [useVoice.subscribe(onChange), useI18n.subscribe(onChange)];
      if (devices) {
        listeners.add(onChange);
        offs.push(() => listeners.delete(onChange), onDevicesChange(refreshDevices));
      }
      return () => offs.forEach((off) => off());
    },
    get() {
      const now = key() + '|' + useI18n.getState().lang;
      if (now !== k) {
        k = now;
        items = build();
      }
      return items;
    },
  };
}

/** The device prefs a menu checks and sets. */
const PREF: Record<AudioKind, 'inputDeviceId' | 'outputDeviceId'> = {
  audioinput: 'inputDeviceId',
  audiooutput: 'outputDeviceId',
};

/**
 * One kind's devices as a submenu: System default, then each device (the
 * one in use checked). A chosen one that is not connected now says so,
 * checked and disabled, as Settings' picker does. A pick applies at once.
 */
function deviceItems(kind: AudioKind): LiveMenuItems {
  const pref = PREF[kind];
  const key = () =>
    JSON.stringify([
      useVoice.getState().prefs[pref],
      known[kind].map((d) => [d.deviceId, d.label]),
    ]);
  return liveItems(
    key,
    () => {
      const { t } = tr();
      const id = useVoice.getState().prefs[pref];
      const list = known[kind];
      const pick = (v: string | null) => () => {
        useVoice.getState().setPrefs({ [pref]: v } as Partial<VoicePrefs>);
        void applyDevices();
      };
      const labeled = list.some((d) => !!d.label);
      const missing = !!id && labeled && !list.some((d) => d.deviceId === id);
      const items: MenuItem[] = [
        { id: 'default', label: t('ns.voice.default'), checked: !id, run: pick(null) },
      ];
      if (missing)
        items.push({
          id: 'missing',
          label: t(kind === 'audioinput' ? 'set.voice.missingOption' : 'vx.q.outputMissing'),
          checked: true,
          disabled: true,
        });
      // the same list as Settings' picker
      list.forEach((d, i) =>
        items.push({
          id: `dev-${i}`,
          label:
            d.label || t(kind === 'audioinput' ? 'ns.voice.micN' : 'ns.voice.spkN', { n: i + 1 }),
          checked: !!id && d.deviceId === id,
          run: pick(d.deviceId || null),
        }),
      );
      return items;
    },
    true,
  );
}

/**
 * The input menu (P21, and Discord's right-click on mute): Input device ›
 * (the microphone in use checked; a pick switches it mid-call), the input
 * mode (voice activation or push to talk, one checked) and Input settings.
 */
export function openInputMenu(anchor: HTMLElement): void {
  const { t } = tr();
  refreshDevices();
  const pick = (mode: 'va' | 'ptt') => () => {
    useVoice.getState().setPrefs({ inputMode: mode });
    setupInput();
  };
  const mode = useVoice.getState().prefs.inputMode;
  openMenu(
    anchor,
    [
      {
        id: 'input-device',
        label: t('vx.q.inputDevice'),
        icon: 'mic',
        submenu: () => deviceItems('audioinput'),
      },
      { sep: true },
      { id: 'va', label: t('ns.voice.va'), icon: 'wave', checked: mode === 'va', run: pick('va') },
      {
        id: 'ptt',
        label: t('ns.voice.ptt'),
        icon: 'cm-key',
        checked: mode === 'ptt',
        run: pick('ptt'),
      },
      { sep: true },
      {
        id: 'input-settings',
        label: t('hubv.vr.inputSettings'),
        icon: 'sliders',
        run: () => openSettings('voice'),
      },
    ],
    { placement: 'top-start', label: t('hubv.vr.inputOptions') },
  );
}

/** The master output volume in percent, as the slider and its value say it. */
const percent = (v: number) =>
  tr().fmt.num(v / 100, { style: 'percent', maximumFractionDigits: 0 });

/**
 * The output menu (Discord's right-click on deafen): Output device › where
 * the browser can choose one (`setSinkId`), the master Output volume (a
 * slider from 0 to 200%, live, which never closes the menu) and Output
 * settings.
 */
export function openOutputMenu(anchor: HTMLElement): void {
  const { t } = tr();
  const pickable = canPickOutput();
  if (pickable) refreshDevices();
  const volume = () => Math.round(useVoice.getState().prefs.outputVolume * 100);
  openMenu(
    anchor,
    liveItems(
      () => String(volume()),
      () => {
        const { t } = tr();
        const items: MenuItem[] = [];
        if (pickable)
          items.push({
            id: 'output-device',
            label: t('vx.q.outputDevice'),
            icon: 'headphones',
            submenu: () => deviceItems('audiooutput'),
          });
        items.push(
          {
            id: 'output-volume',
            label: t('vx.q.outputVolume'),
            range: {
              value: volume(),
              min: 0,
              max: MAX_USER_VOLUME * 100,
              step: 5,
              format: percent,
              onChange: (v) => setOutputVolume(v / 100),
            },
          },
          { sep: true },
          {
            id: 'output-settings',
            label: t('vx.q.outputSettings'),
            icon: 'sliders',
            run: () => openSettings('voice'),
          },
        );
        return items;
      },
    ),
    { placement: 'top-start', label: t('vx.q.outputOptions') },
  );
}
